import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { frameDurationSeconds, timecodeForSeconds } from '@eternally/core';
import { api, ApiError, type ViewerManifest } from '../lib/api.ts';
import { AudioEngine } from '../lib/audio-engine.ts';
import { MediaCache, mediaCacheReady } from '../lib/media-cache.ts';
import { attachVideo } from '../lib/video.ts';
import type { TrackChip } from '../components/track-chip.ts';

const PROGRESS_INTERVAL_MS = 10_000;
const SEEK_STEP_SECONDS = 5;

/**
 * Genlock follower for inactive angles. Every standby picture rolls
 * muted behind the programme, held on the master clock by playback-rate
 * nudges instead of seeks: a seek must decode from the previous
 * keyframe, a rate nudge is free. The engine absorbs a residual of up
 * to 60 ms silently at handover, so the follower aims an order tighter
 * and a switch needs no line-up at all.
 */
const FOLLOW_TICK_MS = 250;
/** Residual the engine absorbs silently; the switch gate. */
const LOCK_WINDOW_SECONDS = 0.06;
/** Inside this band the standby runs at unity rate. */
const LOCK_BAND_SECONDS = 0.02;
/** Proportional gain: rate offset = clock delta x gain. */
const NUDGE_GAIN = 0.5;
/** Rate offsets are invisible on a hidden muted picture; cap anyway. */
const NUDGE_LIMIT = 0.06;
/** Beyond this a hard seek beats nudging (transport seeks, page load). */
const HARD_SEEK_SECONDS = 0.25;
/** Rough compensation for the seek's own latency. */
const SEEK_LEAD_SECONDS = 0.1;

/**
 * Cut to line: the reveal waits for poster and fonts, but never longer
 * than this. A slow network delays content, not the cut.
 */
const REVEAL_DEADLINE_MS = 600;

export async function mountViewer(root: HTMLElement, token: string): Promise<void> {
  // Cut to line. The player geometry is a pure function of route and
  // viewport, so the scenography paints complete before any network
  // round trip: marks on their final positions, the picture plate dark
  // like a monitor awaiting signal. The manifest request itself
  // answers the auth question, so one round trip is the common path;
  // only a 401 costs a second fetch, for the sign-in panel's label.
  renderPlayerShell(root);

  let manifest: ViewerManifest;
  try {
    manifest = await api.manifest(token);
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      const meta = await api.shareMeta(token).catch(() => null);
      cutTo(() => {
        renderShell(root);
        const main = root.querySelector('main') as HTMLElement;
        if (meta) {
          mountAuth(main, token, meta.label);
        } else {
          renderMessage(main, 'This link is not available');
        }
      });
      return;
    }
    cutTo(() => {
      renderShell(root);
      renderMessage(
        root.querySelector('main') as HTMLElement,
        error instanceof ApiError ? error.message : 'This link is not available',
      );
    });
    return;
  }

  if (manifest.ingest && !manifest.ingest.ready) {
    cutTo(() => {
      renderShell(root);
      mountArriving(root.querySelector('main') as HTMLElement, token, manifest);
    });
    return;
  }
  await mountPlayer(root.querySelector('main') as HTMLElement, token, manifest);
}

function renderShell(root: HTMLElement): void {
  // The header carries fit-video here too: the marks hold the exact
  // positions the player scenography gave them, so a scene change
  // (sign-in, arriving, message) never nudges the constant furniture.
  root.innerHTML = `
    <div class="frame">
      <header class="fit-video">
        <eternally-wordmark></eternally-wordmark>
        <div class="brandmark" role="img" aria-label="Thåst Works"><span>Thåst</span><span>Works</span></div>
      </header>
      <main></main>
      <footer></footer>
    </div>
  `;
}

/**
 * The player scenography, painted before any network round trip. The
 * marks are born on their final positions and the picture plate stands
 * dark where the picture will land. The real player replaces the plate
 * with an identical box, so nothing ever moves after first paint;
 * content only ever dissolves in.
 */
function renderPlayerShell(root: HTMLElement): void {
  root.innerHTML = `
    <div class="frame player-frame" data-stage="lineup">
      <header class="fit-video">
        <eternally-wordmark></eternally-wordmark>
        <div class="brandmark" role="img" aria-label="Thåst Works"><span>Thåst</span><span>Works</span></div>
      </header>
      <main>
        <div class="video-stack"></div>
      </main>
      <footer></footer>
    </div>
  `;
}

/**
 * Deliberate scene changes (player shell to sign-in, to arriving, to a
 * message) ride the View Transitions API where the browser offers it:
 * one composed cross-fade instead of a DOM pop. Elsewhere the update
 * simply runs, which is the same scene minus the fade.
 */
function cutTo(update: () => void): void {
  const doc = document as Document & { startViewTransition?: (update: () => void) => void };
  if (typeof doc.startViewTransition === 'function') {
    doc.startViewTransition(update);
  } else {
    update();
  }
}

function renderMessage(main: HTMLElement, text: string): void {
  main.innerHTML = `<div class="panel"><p>${escapeHtml(text)}</p></div>`;
}

/* Authentication panel: one biometric confirmation from link to video. */

function mountAuth(main: HTMLElement, token: string, label: string): void {
  main.innerHTML = `
    <div class="panel">
      <h1>${escapeHtml(label)}</h1>
      <p>Enter your name to continue. Access is confirmed with your device's screen lock.</p>
      <label class="field">
        <span>Name</span>
        <input type="text" name="displayName" autocomplete="name" maxlength="64" />
      </label>
      <button class="button" data-action="register">Continue</button>
      <button class="button secondary" data-action="login">I have been here before</button>
      <button class="button secondary" data-action="magic">Use an email link instead</button>
      <p class="notice" hidden></p>
    </div>
  `;

  const panel = main.querySelector('.panel') as HTMLElement;
  const nameInput = panel.querySelector('input[name="displayName"]') as HTMLInputElement;
  const notice = panel.querySelector('.notice') as HTMLElement;

  const showNotice = (text: string, isError = false) => {
    notice.textContent = text;
    notice.hidden = false;
    notice.classList.toggle('error', isError);
  };

  panel.querySelector('[data-action="register"]')?.addEventListener('click', async () => {
    const displayName = nameInput.value.trim();
    if (!displayName) {
      showNotice('Enter your name first', true);
      nameInput.focus();
      return;
    }
    try {
      const { challengeId, options } = await api.registerOptions(token, displayName);
      const response = await startRegistration({ optionsJSON: options as never });
      await api.registerVerify(token, challengeId, response);
      await mountPlayer(main, token);
    } catch (error) {
      showNotice(authErrorText(error), true);
    }
  });

  panel.querySelector('[data-action="login"]')?.addEventListener('click', async () => {
    try {
      const { challengeId, options } = await api.loginOptions(token);
      const response = await startAuthentication({ optionsJSON: options as never });
      await api.loginVerify(token, challengeId, response);
      await mountPlayer(main, token);
    } catch (error) {
      showNotice(authErrorText(error), true);
    }
  });

  panel.querySelector('[data-action="magic"]')?.addEventListener('click', () => {
    mountMagicForm(main, token, label, nameInput.value.trim());
  });
}

function mountMagicForm(main: HTMLElement, token: string, label: string, presetName: string): void {
  main.innerHTML = `
    <div class="panel">
      <h1>${escapeHtml(label)}</h1>
      <p>A one-time link will be sent to your email address. It is valid for fifteen minutes.</p>
      <label class="field">
        <span>Name</span>
        <input type="text" name="displayName" autocomplete="name" maxlength="64"
               value="${escapeHtml(presetName)}" />
      </label>
      <label class="field">
        <span>Email</span>
        <input type="email" name="email" autocomplete="email" maxlength="254" />
      </label>
      <button class="button" data-action="send">Send link</button>
      <button class="button secondary" data-action="back">Back</button>
      <p class="notice" hidden></p>
    </div>
  `;
  const panel = main.querySelector('.panel') as HTMLElement;
  const notice = panel.querySelector('.notice') as HTMLElement;

  panel.querySelector('[data-action="send"]')?.addEventListener('click', async () => {
    const displayName = (panel.querySelector('input[name="displayName"]') as HTMLInputElement).value.trim();
    const email = (panel.querySelector('input[name="email"]') as HTMLInputElement).value.trim();
    if (!displayName || !email) {
      notice.textContent = 'Name and email are both required';
      notice.hidden = false;
      notice.classList.add('error');
      return;
    }
    try {
      const result = await api.magicRequest(token, email, displayName);
      notice.classList.remove('error');
      notice.hidden = false;
      // The devLink shortcut exists only in development builds of the
      // API; production responses never carry it.
      notice.textContent = result.devLink
        ? `Development link: ${result.devLink}`
        : 'Sent. Check your inbox and open the link on this device.';
    } catch (error) {
      notice.textContent = authErrorText(error);
      notice.hidden = false;
      notice.classList.add('error');
    }
  });

  panel.querySelector('[data-action="back"]')?.addEventListener('click', () => {
    mountAuth(main, token, label);
  });
}

function authErrorText(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.name === 'NotAllowedError') {
    return 'The passkey prompt was dismissed. Try again.';
  }
  return 'Something went wrong. Try again.';
}

/* The player itself. */

async function mountPlayer(
  main: HTMLElement,
  token: string,
  prefetched?: ViewerManifest,
): Promise<void> {
  let manifest: ViewerManifest;
  if (prefetched) {
    manifest = prefetched;
  } else {
    try {
      manifest = await api.manifest(token);
    } catch (error) {
      renderMessage(main, error instanceof ApiError ? error.message : 'The material is not available');
      return;
    }
  }

  if (manifest.ingest && !manifest.ingest.ready) {
    mountArriving(main, token, manifest);
    return;
  }

  // The technical line reads like a tape label: curated facts first,
  // then TC in / TC out / Dur derived from the manifest itself, never
  // hand-written and therefore never wrong.
  const fields: { label: string; value: string }[] = [...(manifest.meta ?? [])];
  if (manifest.timecode) {
    const spec = manifest.timecode;
    const zero = spec.dropFrame ? '00:00:00;00' : '00:00:00:00';
    fields.push(
      { label: 'TC in', value: spec.start },
      { label: 'TC out', value: timecodeForSeconds(spec, manifest.durationSeconds) },
      { label: 'Dur', value: timecodeForSeconds({ ...spec, start: zero }, manifest.durationSeconds) },
    );
  }
  // Whether the readout has anything to report is a synchronous fact,
  // so it claims its place in the tape label before first paint. The
  // line is composed once and never reflows: either the slot is there
  // from the cut, or it stays away for the whole sitting.
  const cacheCapable = mediaCacheReady(manifest);
  const localFieldHtml = cacheCapable
    ? `<span class="field" data-role="local"><span class="field-label">Local</span><span class="field-value local-value">0 %</span></span>`
    : '';
  const fieldsHtml =
    fields.length > 0 || cacheCapable
      ? `<p class="player-fields" data-reveal>${fields
          .map(
            (field) =>
              `<span class="field"><span class="field-label">${escapeHtml(field.label)}</span><span class="field-value">${escapeHtml(field.value)}</span></span>`,
          )
          .join('')}${localFieldHtml}</p>`
      : '';

  main.innerHTML = `
    <div class="video-stack">
      <video class="player-video" data-active="false" playsinline muted></video>
      <p class="player-osd" hidden>Opening<span class="osd-dots"></span></p>
    </div>
    <div class="scrub fit-video" data-reveal role="slider" aria-label="Position"
         aria-valuemin="0" aria-valuemax="100" aria-valuenow="0" tabindex="0">
      <div class="scrub-track"><div class="scrub-fill"></div></div>
      <div class="scrub-tip" hidden></div>
    </div>
    <div class="player-meta fit-video" data-reveal>
      <p class="player-title">${escapeHtml(manifest.title)}</p>
      <div class="transport" role="group" aria-label="Transport">
        <button type="button" data-t="rew" aria-label="Rewind">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M7 3 1 8l6 5V3zm7.5 0-6 5 6 5V3z" fill="currentColor"/></svg>
        </button>
        <button type="button" data-t="play" aria-label="Play">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 2.5 13.5 8 4 13.5v-11z" fill="currentColor"/></svg>
        </button>
        <button type="button" data-t="pause" aria-label="Pause">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 2.5h2.8v11H4zm5.2 0H12v11H9.2z" fill="currentColor"/></svg>
        </button>
        <button type="button" data-t="ff" aria-label="Fast forward">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M9 3l6 5-6 5V3zM1.5 3l6 5-6 5V3z" fill="currentColor"/></svg>
        </button>
        <button type="button" data-t="fs" aria-label="Fullscreen">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 6V2h4M10 2h4v4M14 10v4h-4M6 14H2v-4" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
        </button>
      </div>
      <p class="player-tc" hidden></p>
    </div>
    <div class="controls fit-video" data-reveal>
      <div class="control-group" data-role="video-group" hidden>
        <svg class="group-icon" viewBox="0 0 20 14" aria-hidden="true">
          <rect x="0.5" y="0.5" width="19" height="13" rx="1.5" fill="none" stroke="currentColor" />
          <rect x="2.5" y="2.5" width="2.1" height="9" fill="currentColor" opacity="0.95" />
          <rect x="5.1" y="2.5" width="2.1" height="9" fill="currentColor" opacity="0.72" />
          <rect x="7.7" y="2.5" width="2.1" height="9" fill="currentColor" opacity="0.52" />
          <rect x="10.3" y="2.5" width="2.1" height="9" fill="currentColor" opacity="0.36" />
          <rect x="12.9" y="2.5" width="2.1" height="9" fill="currentColor" opacity="0.22" />
          <rect x="15.5" y="2.5" width="2" height="9" fill="currentColor" opacity="0.1" />
        </svg>
        <div class="angles" role="group" aria-label="Video angle"></div>
      </div>
      <div class="control-group">
        <svg class="group-icon" viewBox="0 0 20 14" aria-hidden="true">
          <rect x="0.5" y="0.5" width="8.5" height="13" rx="2.8" fill="none" stroke="currentColor" />
          <circle cx="4.75" cy="4.3" r="1.2" fill="none" stroke="currentColor" />
          <circle cx="4.75" cy="9.2" r="2.5" fill="none" stroke="currentColor" />
          <circle cx="4.75" cy="9.2" r="0.7" fill="currentColor" opacity="0.5" />
          <rect x="11" y="0.5" width="8.5" height="13" rx="2.8" fill="none" stroke="currentColor" />
          <circle cx="15.25" cy="4.3" r="1.2" fill="none" stroke="currentColor" />
          <circle cx="15.25" cy="9.2" r="2.5" fill="none" stroke="currentColor" />
          <circle cx="15.25" cy="9.2" r="0.7" fill="currentColor" opacity="0.5" />
        </svg>
        <div class="chips" role="group" aria-label="Audio tracks"></div>
      </div>
    </div>
    ${fieldsHtml}
  `;
  // In the player view the header locks to the picture column: the
  // marks share their edges with the picture, the title and the TC.
  // The frame itself switches to the fixed vertical rhythm.
  document.querySelector('.frame > header')?.classList.add('fit-video');
  const frame = document.querySelector('.frame') as HTMLElement | null;
  frame?.classList.add('player-frame');
  // Hold the chrome dark while the composition lines up; the cut
  // below releases everything in one dissolve.
  frame?.setAttribute('data-stage', 'lineup');

  const video = main.querySelector('video') as HTMLVideoElement;
  if (manifest.video.posterUrl) {
    video.poster = manifest.video.posterUrl;
  }
  const stack = main.querySelector('.video-stack') as HTMLElement;
  const angleRow = main.querySelector('.angles') as HTMLElement;
  const chipRow = main.querySelector('.chips') as HTMLElement;
  const notice = main.querySelector('.player-osd') as HTMLElement;

  // The active element changes on angle switches; everything that
  // reads the transport goes through this reference.
  const videoRef: VideoRef = { current: video };

  // Angle identity and switch state live at player scope because the
  // local media cache reads both: it walks the active angle first and
  // stands aside entirely while a switch is in flight.
  const angles = manifest.video.angles ?? [];
  let currentAngleId = angles[0]?.id ?? 'main';
  let switching = false;
  let cache: MediaCache | null = null;

  /**
   * Every deliberate jump goes through here. One place clamps the
   * target, and one place tells the cache where the viewer came from —
   * which is what lets a skipped region be closed from both ends.
   */
  const seekTo = (seconds: number): void => {
    const active = videoRef.current;
    const origin = active.currentTime;
    const target = Math.min(Math.max(0, seconds), manifest.durationSeconds);
    active.currentTime = target;
    cache?.noteSeek(origin, target);
  };

  // The cut. Poster decoded, fonts ready, manifest already parsed:
  // the whole composition dissolves in as one event on the house
  // 150 ms budget. The deadline keeps a slow network from ever
  // blocking the cut; whatever misses it lands in a live picture.
  const lineUp: Promise<unknown>[] = [document.fonts.ready];
  if (manifest.video.posterUrl) {
    const posterImage = new Image();
    // The poster must win the race against the video and audio
    // downloads it shares the link with; high priority says so.
    (posterImage as HTMLImageElement & { fetchPriority?: string }).fetchPriority = 'high';
    posterImage.src = manifest.video.posterUrl;
    lineUp.push(posterImage.decode().catch(() => {}));
  }
  void Promise.race([
    Promise.all(lineUp),
    new Promise((resolve) => window.setTimeout(resolve, REVEAL_DEADLINE_MS)),
  ]).then(() => {
    frame?.removeAttribute('data-stage');
    videoRef.current.dataset.active = 'true';
  });

  // No native control overlay: the picture is clean, the surface
  // itself is the transport. Click or tap toggles; keyboard does the
  // rest.
  stack.addEventListener('click', () => {
    const active = videoRef.current;
    if (active.paused) {
      void active.play();
    } else {
      active.pause();
    }
  });

  // Deck-style transport: separate play and pause, the engaged mode
  // lit like a lamp, fullscreen last.
  const transport = main.querySelector('.transport') as HTMLElement;
  transport.addEventListener('click', (event) => {
    const button = (event.target as HTMLElement).closest('button[data-t]') as HTMLButtonElement | null;
    if (!button) {
      return;
    }
    const active = videoRef.current;
    switch (button.dataset.t) {
      case 'rew':
        seekTo(active.currentTime - SEEK_STEP_SECONDS);
        break;
      case 'play':
        void active.play();
        break;
      case 'pause':
        active.pause();
        break;
      case 'ff':
        seekTo(active.currentTime + SEEK_STEP_SECONDS);
        break;
      case 'fs':
        void stack.requestFullscreen().catch(() => {});
        break;
    }
  });
  const playButton = transport.querySelector('[data-t="play"]') as HTMLElement;
  const pauseButton = transport.querySelector('[data-t="pause"]') as HTMLElement;
  window.setInterval(() => {
    const paused = videoRef.current.paused;
    playButton.classList.toggle('lit', !paused);
    pauseButton.classList.toggle('lit', paused);
  }, 250);

  // The scrub bar: position as a quiet fact, seek on click, and the
  // hovered instant answered in source timecode.
  const scrub = main.querySelector('.scrub') as HTMLElement;
  const scrubFill = main.querySelector('.scrub-fill') as HTMLElement;
  const scrubTip = main.querySelector('.scrub-tip') as HTMLElement;
  const duration = manifest.durationSeconds || 1;

  const ratioFrom = (clientX: number): number => {
    const rect = scrub.getBoundingClientRect();
    return Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
  };
  const labelFor = (seconds: number): string => {
    if (manifest.timecode) {
      return timecodeForSeconds(manifest.timecode, Math.max(0, seconds));
    }
    const m = Math.floor(seconds / 60);
    const s = String(Math.floor(seconds % 60)).padStart(2, '0');
    return `${m}:${s}`;
  };
  scrub.addEventListener('click', (event) => {
    seekTo(ratioFrom(event.clientX) * duration);
  });
  scrub.addEventListener('pointermove', (event) => {
    const ratio = ratioFrom(event.clientX);
    scrubTip.hidden = false;
    scrubTip.style.left = `${ratio * 100}%`;
    scrubTip.textContent = labelFor(ratio * duration);
  });
  scrub.addEventListener('pointerleave', () => {
    scrubTip.hidden = true;
  });
  const progressTick = () => {
    if (!scrubFill.isConnected) {
      return;
    }
    const ratio = Math.min(videoRef.current.currentTime / duration, 1);
    scrubFill.style.width = `${ratio * 100}%`;
    scrub.setAttribute('aria-valuenow', String(Math.round(ratio * 100)));
    window.requestAnimationFrame(progressTick);
  };
  window.requestAnimationFrame(progressTick);

  const slots = new Map<string, { video: HTMLVideoElement; detach: () => void }>();
  slots.set(currentAngleId, {
    video,
    detach: await attachVideo(video, manifest.video.playlistUrl),
  });

  const engine = new AudioEngine(video, manifest.tracks, manifest.targetLufs);

  // Angle switching, vision-mixer style: every inactive angle rolls
  // muted and hidden, genlocked to the master by the follower below,
  // so a switch is a dissolve and a clock handover, nothing else. The
  // audio graph is untouched throughout; the engine merely follows the
  // new element's clock.
  if (angles.length > 1) {
    main.querySelector('[data-role="video-group"]')?.removeAttribute('hidden');
    const angleButtons = new Map<string, HTMLButtonElement>();

    const slotFor = async (angle: { id: string; playlistUrl: string; posterUrl?: string }) => {
      const existing = slots.get(angle.id);
      if (existing) {
        return existing;
      }
      const standby = document.createElement('video');
      standby.className = 'player-video';
      standby.playsInline = true;
      standby.muted = true;
      if (angle.posterUrl) {
        standby.poster = angle.posterUrl;
      }
      standby.dataset.active = 'false';
      stack.append(standby);
      const slot = { video: standby, detach: await attachVideo(standby, angle.playlistUrl) };
      slots.set(angle.id, slot);
      return slot;
    };

    const selectAngle = async (angleId: string): Promise<void> => {
      const target = angles.find((angle) => angle.id === angleId);
      if (!target || angleId === currentAngleId || switching) {
        return;
      }
      switching = true;
      try {
        const master = videoRef.current;
        const { video: standby } = await slotFor(target);

        const locked =
          !standby.paused &&
          standby.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA &&
          Math.abs(standby.currentTime - master.currentTime) <= LOCK_WINDOW_SECONDS;

        if (master.paused || master.ended) {
          standby.pause();
          standby.currentTime = master.currentTime;
          await waitFor(standby, 'seeked', 2000);
        } else if (!locked) {
          // The follower has not locked this angle yet (fresh load, or
          // right after a transport seek): line it up the long way.
          // A resolved play() already confirms the picture is rolling;
          // the 'playing' event fires before the promise settles, so
          // waiting for it afterwards can only ever hit the timeout.
          // The event wait remains solely as the rejection fallback.
          standby.currentTime = master.currentTime;
          const started = await standby.play().then(() => true, () => false);
          if (!started) {
            await waitFor(standby, 'playing', 3000);
          }
          // Converge on the master clock: each pass measures how far
          // the previous seek landed from target and adapts the lead
          // that compensates for the seek's own latency. Within three
          // passes the residual is small enough for the engine to
          // absorb silently into its anchor.
          let lead = 0.05;
          for (let attempt = 0; attempt < 3; attempt += 1) {
            const delta = standby.currentTime - master.currentTime;
            if (Math.abs(delta) <= 0.03) {
              break;
            }
            standby.currentTime = master.currentTime + lead;
            await waitFor(standby, 'seeked', 2000);
            const residual = standby.currentTime - master.currentTime;
            lead = Math.min(Math.max(lead - residual, 0), 0.3);
          }
        }

        standby.playbackRate = master.playbackRate;
        standby.dataset.active = 'true';
        master.dataset.active = 'false';
        videoRef.current = standby;
        engine.setClockSource(standby);
        currentAngleId = angleId;
        for (const [id, button] of angleButtons) {
          button.setAttribute('aria-pressed', id === angleId ? 'true' : 'false');
        }
        // The outgoing picture is not paused: it becomes the rolling
        // standby, and the follower keeps it genlocked for the switch
        // back.
      } finally {
        switching = false;
      }
    };

    for (const [index, angle] of angles.entries()) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'angle';
      button.textContent = angle.label;
      button.setAttribute('aria-pressed', index === 0 ? 'true' : 'false');
      button.addEventListener('click', () => {
        void selectAngle(angle.id);
      });
      angleButtons.set(angle.id, button);
      angleRow.append(button);
    }

    // The genlock follower. Standby pictures play and pause with the
    // master and are held on its clock: macroscopic offsets (page
    // load, transport seeks) jump once with a small latency lead, the
    // remainder resolves through proportional rate nudges. The cost of
    // a hot standby is honest: the inactive stream decodes
    // continuously, exactly as a mixer keeps its sources rolling.
    const followTick = () => {
      if (switching) {
        return;
      }
      const master = videoRef.current;
      for (const [id, slot] of slots) {
        if (id === currentAngleId) {
          continue;
        }
        const standby = slot.video;
        if (master.paused || master.ended) {
          if (!standby.paused) {
            standby.pause();
          }
          continue;
        }
        if (standby.paused) {
          void standby.play().catch(() => {});
        }
        const delta = standby.currentTime - master.currentTime;
        if (Math.abs(delta) > HARD_SEEK_SECONDS) {
          standby.currentTime = master.currentTime + SEEK_LEAD_SECONDS;
        } else if (Math.abs(delta) > LOCK_BAND_SECONDS) {
          const correction = Math.min(NUDGE_LIMIT, Math.max(-NUDGE_LIMIT, delta * NUDGE_GAIN));
          standby.playbackRate = master.playbackRate - correction;
        } else if (standby.playbackRate !== master.playbackRate) {
          standby.playbackRate = master.playbackRate;
        }
      }
    };
    // Standby attach waits for the first play: the opening burst
    // belongs to the programme picture, the poster and the priority
    // track. Once the transport rolls the standbys join and genlock
    // within seconds, long before a hand reaches the angle row.
    video.addEventListener(
      'play',
      () => {
        void (async () => {
          for (const angle of angles) {
            await slotFor(angle);
          }
        })();
      },
      { once: true },
    );
    window.setInterval(followTick, FOLLOW_TICK_MS);
  }

  // The local media cache. Started, never awaited: the player is
  // already rolling by the time the worker answers, and the walk
  // happens strictly in the slack between playback's own downloads.
  // The engine's background chain owns the audio files until it says
  // otherwise, so the two never pull the same track at once.
  const localField = main.querySelector('[data-role="local"]') as HTMLElement | null;
  const localValue = main.querySelector('.local-value') as HTMLElement | null;
  let audioLoadComplete = false;
  engine.onLoadComplete = () => {
    audioLoadComplete = true;
    cache?.releaseAudio();
  };
  void MediaCache.start(manifest, {
    activeAngleId: () => currentAngleId,
    playheadSeconds: () => videoRef.current.currentTime,
    bufferedAheadSeconds: () => bufferedAheadSeconds(videoRef.current),
    paused: () => videoRef.current.paused,
    seeking: () => videoRef.current.seeking,
    switching: () => switching,
    refreshManifest: () => api.manifest(token),
  }).then((started) => {
    if (!started) {
      // A worker was in control and then failed to answer: rare enough
      // to be a fault rather than a state. The readout goes rather than
      // stand at a figure that will never move.
      localField?.remove();
      return;
    }
    cache = started;
    started.onStats = (stats) => {
      if (localValue) {
        localValue.textContent = `${stats.percent} %`;
      }
    };
    if (audioLoadComplete) {
      started.releaseAudio();
    }
  });

  // Source timecode readout, driven by the video clock every frame.
  const tcElement = main.querySelector('.player-tc') as HTMLElement;
  if (manifest.timecode) {
    const spec = manifest.timecode;
    tcElement.hidden = false;
    const tick = () => {
      if (!tcElement.isConnected) {
        return;
      }
      tcElement.textContent = timecodeForSeconds(spec, Math.max(0, videoRef.current.currentTime));
      window.requestAnimationFrame(tick);
    };
    window.requestAnimationFrame(tick);
  }

  const chips = new Map<string, TrackChip>();
  manifest.tracks.forEach((track) => {
    const chip = document.createElement('eternally-track-chip') as TrackChip;
    chip.setAttribute('label', track.label);
    chip.dataset.trackId = track.id;
    chip.addEventListener('track-toggle', () => {
      engine.toggle(track.id);
      refreshChips();
    });
    chip.addEventListener('track-solo', () => {
      engine.solo(track.id);
      refreshChips();
    });
    chips.set(track.id, chip);
    chipRow.append(chip);
  });

  const refreshChips = () => {
    for (const [id, chip] of chips) {
      chip.setActive(engine.active.has(id));
    }
  };

  notice.hidden = false;
  // Line-up in progress, marked the classic terminal way: one dot,
  // two, three, none. The dot field is fixed-width so the plate
  // itself never moves.
  const osdDots = notice.querySelector('.osd-dots') as HTMLElement;
  let dotCount = 0;
  const dotsTimer = window.setInterval(() => {
    dotCount = (dotCount + 1) % 4;
    osdDots.textContent = '.'.repeat(dotCount);
  }, 500);

  // Chips wake as their tracks decode: the priority track releases the
  // transport, the rest join the running mix in flight. On a thin link
  // this is the difference between one download and five before sound.
  const readyIds = new Set<string>();
  const refreshReady = () => {
    for (const [id, chip] of chips) {
      chip.setReady(readyIds.has(id));
    }
  };
  refreshReady();
  engine.onTrackReady = (id) => {
    readyIds.add(id);
    refreshReady();
  };
  try {
    await engine.load(manifest.tracks[0]?.id);
  } catch {
    window.clearInterval(dotsTimer);
    notice.textContent = 'Audio failed to load. Reload the page to try again.';
    notice.classList.add('error');
    return;
  }
  window.clearInterval(dotsTimer);
  notice.hidden = true;

  // The first track is the editorial default; the operator controls
  // ordering from the console.
  const first = manifest.tracks[0];
  engine.setInitialActive(first ? [first.id] : []);
  refreshChips();

  bindKeyboard(videoRef, engine, manifest, refreshChips, seekTo);
  bindProgress(videoRef, token);

  window.addEventListener(
    'beforeunload',
    () => {
      for (const slot of slots.values()) {
        slot.detach();
      }
      cache?.stop();
      void engine.dispose();
    },
    { once: true },
  );
}

/**
 * Seconds of continuously buffered material ahead of the playhead. The
 * prefetcher uses it as its yield signal: below the floor the link
 * belongs to playback alone.
 */
function bufferedAheadSeconds(video: HTMLVideoElement): number {
  const position = video.currentTime;
  for (let index = 0; index < video.buffered.length; index += 1) {
    if (video.buffered.start(index) <= position && position <= video.buffered.end(index)) {
      return video.buffered.end(index) - position;
    }
  }
  return 0;
}

interface VideoRef {
  current: HTMLVideoElement;
}

/** Await an event with a deterministic timeout so a switch never hangs. */
function waitFor(element: HTMLElement, event: string, timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    const done = () => {
      window.clearTimeout(timer);
      element.removeEventListener(event, done);
      resolve();
    };
    const timer = window.setTimeout(done, timeoutMs);
    element.addEventListener(event, done, { once: true });
  });
}

/**
 * Shown while the material is still arriving in storage. The share is
 * valid from the moment the manifest lands, so a link can go out
 * before the payload has; this view keeps the promise honest and
 * hands over to the player on its own the moment everything is up.
 */
function mountArriving(main: HTMLElement, token: string, manifest: ViewerManifest): void {
  const percent = Math.floor((manifest.ingest?.progress ?? 0) * 100);
  main.innerHTML = `
    <div class="panel">
      <h1>${escapeHtml(manifest.title)}</h1>
      <p>Material is arriving</p>
      <div class="arriving-bar" role="progressbar" aria-valuemin="0" aria-valuemax="100"
           aria-valuenow="${percent}"><span style="width: ${percent}%"></span></div>
      <p class="arriving-pct">${percent} %</p>
    </div>
  `;
  const bar = main.querySelector('.arriving-bar span') as HTMLElement;
  const pct = main.querySelector('.arriving-pct') as HTMLElement;

  let settled = false;
  const poll = async () => {
    if (settled) {
      return;
    }
    try {
      const fresh = await api.manifest(token);
      const value = Math.floor((fresh.ingest?.progress ?? 0) * 100);
      bar.style.width = `${value}%`;
      pct.textContent = `${value} %`;
      main.querySelector('.arriving-bar')?.setAttribute('aria-valuenow', String(value));
      if (!fresh.ingest || fresh.ingest.ready) {
        settled = true;
        window.clearInterval(timer);
        document.removeEventListener('visibilitychange', onVisible);
        await mountPlayer(main, token);
      }
    } catch {
      // A failed poll is not fatal; the next tick tries again.
    }
  };
  const timer = window.setInterval(() => {
    void poll();
  }, 10_000);
  // Browsers throttle timers hard in hidden tabs, so a backgrounded
  // tab can show a stale figure for minutes. Polling on refocus snaps
  // it to the truth the instant the viewer returns.
  const onVisible = () => {
    if (!document.hidden) {
      void poll();
    }
  };
  document.addEventListener('visibilitychange', onVisible);
}

function bindKeyboard(
  videoRef: VideoRef,
  engine: AudioEngine,
  manifest: ViewerManifest,
  refreshChips: () => void,
  seekTo: (seconds: number) => void,
): void {
  window.addEventListener('keydown', (event) => {
    const target = event.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
      return;
    }
    const video = videoRef.current;
    if (event.key === ' ') {
      event.preventDefault();
      if (video.paused) {
        void video.play();
      } else {
        video.pause();
      }
      return;
    }
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      const direction = event.key === 'ArrowLeft' ? -1 : 1;
      seekTo(video.currentTime + direction * SEEK_STEP_SECONDS);
      return;
    }
    if (event.key === ',' || event.key === '.') {
      // Frame stepping needs the exact source rate; without a timecode
      // spec there is no honest frame duration to step by.
      if (!manifest.timecode) {
        return;
      }
      event.preventDefault();
      videoRef.current.pause();
      const step = frameDurationSeconds(manifest.timecode.frameRate);
      const direction = event.key === ',' ? -1 : 1;
      seekTo(videoRef.current.currentTime + direction * step);
      return;
    }
    if (/^[1-9]$/.test(event.key)) {
      const track = manifest.tracks[Number(event.key) - 1];
      if (track) {
        engine.toggle(track.id);
        refreshChips();
      }
    }
  });
}

function bindProgress(videoRef: VideoRef, token: string): void {
  let lastSent = -1;
  let completeSent = false;
  window.setInterval(() => {
    const video = videoRef.current;
    if (video.ended && !completeSent) {
      completeSent = true;
      void api.progress(token, video.duration, true).catch(() => {});
      return;
    }
    if (!video.ended) {
      completeSent = false;
    }
    if (video.paused || video.currentTime === lastSent) {
      return;
    }
    lastSent = video.currentTime;
    void api.progress(token, video.currentTime).catch(() => {
      // Progress is best-effort telemetry; playback must never suffer
      // for a failed log write.
    });
  }, PROGRESS_INTERVAL_MS);
}

function escapeHtml(text: string): string {
  return text
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}
